import { Button, Card, Col, Container, Form, InputGroup, Modal, Row } from "react-bootstrap"
import { useUserContext } from "../user/UserContext"
import { useLoading } from "../loading/LoadingContext"
import { useEffect, useRef, useState } from "react"
import { apiFetchRaw } from "../utility/fetch"

function parseRmaScan(scan: string) {
  const poMatch = scan.match(/34Z019K([A-Z0-9_]+)/)
  const poNumber = poMatch?.[1] ?? null

  const returnCenterMatch = scan.match(/ZGD(\d{5})/)
  const returnCenter = returnCenterMatch?.[1] ?? null

  const dateMatch = scan.match(/LLC(\d{4})/)
  const date = dateMatch?.[1] ?? null

  const trackingMatch = scan.match(/(\d{12})34Z019K/)
  const trackingNumber = trackingMatch?.[1] ?? null

  return {
    trackingNumber,
    poNumber,
    returnCenter,
    date,
  }
}

function Returns() {
  const { userData } = useUserContext()
  const { toggleLoading } = useLoading()

  const [scanText, setScanText] = useState("")
  const [parsedData, setParsedData] = useState<ReturnType<typeof parseRmaScan> | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [orderInfo, setOrderInfo] = useState<any>(null)

  const scanBufferRef = useRef("")

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Shift" ||
      e.key.startsWith("F") ||
      e.key === "Control" ||
      e.key === "Alt" ||
      e.key === "Meta"
    ) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    if (e.key === "Enter") {
      e.preventDefault()
      e.stopPropagation()

      const fullScan = scanBufferRef.current.trim()
      setScanText(fullScan)
      if (fullScan.length > 20) {
        const parsed = parseRmaScan(fullScan)
        setParsedData(parsed)
        setShowModal(true)
      } else {
        setParsedData(null)
        setShowModal(false)
      }

      scanBufferRef.current = ""
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value
    const filtered = inputVal.replace(/[\x00-\x1F\x7F]|Shift|F\d{1,2}/g, "")
    scanBufferRef.current = filtered
  }

  const fetchOrderInfo = async (poNumber: string) => {
    if (!poNumber) return

    toggleLoading(true)

    const cleanedPoNumber = parsedData.poNumber?.split('_')[0] ?? ""

    const soqlQuery = `Select
      Id,
      Product2.Id,
      Product_MPN__c,
      Quantity,
      TotalPrice,
      Sales_Source__c, 
      Order.Id,
      Order.Name,
      Order.Customer_Name__c,
      Order.Customer_Phone_Number__c
      From
      OrderItem
      Where Order.Name = '${cleanedPoNumber}'`

    try {
      const response = await apiFetchRaw({
        endpoint: `salesforceQuery`,
        method: 'POST',
        body: JSON.stringify({ soql: soqlQuery }),
        accessToken: userData.accessToken
      })

      console.log(response)

      const result = JSON.parse(response.body as string)
      setOrderInfo(result)
    } catch (err) {
      console.error("Failed to fetch order info:", err)
      setOrderInfo(null)
    } finally {
      toggleLoading(false)
    }
  }

  // 🔄 Fetch Salesforce data when PO is available
  useEffect(() => {
    if (parsedData?.poNumber) {
      fetchOrderInfo(parsedData.poNumber)
    }
  }, [parsedData?.poNumber, userData.accessToken])

  return (
    <Container className="fluid justify-content-center align-items-center mt-4">
      <Row>
        <Col>
          <Card className="text-center p-3 shadow-sm">
            <Card.Body>
              <Card.Title>Returns</Card.Title>
              <InputGroup className="mb-3">
                <InputGroup.Text id="inputScan">Scan</InputGroup.Text>
                <Form.Control
                  aria-describedby="inputScan"
                  placeholder="Scan RMA barcode here"
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  autoFocus
                />
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {parsedData && (
      <Row className="mt-4">
        <Col>
          <Card className="p-3 shadow-sm">
            <Card.Body>
              <Card.Title>Parsed RMA Scan</Card.Title>
              <div className="fs-4" ><strong>Tracking Number:</strong> {parsedData.trackingNumber ?? "N/A"}</div>
              <div className="fs-4" ><strong>PO Number:</strong> {parsedData.poNumber ?? "N/A"}</div>
              <div className="fs-4" ><strong>Return Center ID:</strong> {parsedData.returnCenter ?? "N/A"}</div>
              <div className="fs-4" ><strong>Date (MMDD):</strong> {parsedData.date ?? "N/A"}</div>
              <Button
                variant="secondary"
                size="sm"
                className="my-3"
                onClick={() => parsedData?.poNumber && fetchOrderInfo(parsedData.poNumber)}
              >
                Retry Fetch from Salesforce
              </Button>
              <hr />
              {orderInfo ? (
                <>
                  <div className="fs-4" ><strong>Customer Name:</strong> {orderInfo.records?.[0]?.Order?.Customer_Name__c ?? "N/A"}</div>
                  <div className="fs-4" ><strong>Phone:</strong> {orderInfo.records?.[0]?.Order?.Customer_Phone_Number__c ?? "N/A"}</div>
                  <div className="fs-4" ><strong>PO Number:</strong> {orderInfo.records?.[0]?.Order?.Name ?? "N/A"}</div>
                  <hr />
                  <div className="fs-4" ><strong>Items:</strong></div>
                  {orderInfo.records.map((item, idx) => (
                    <div key={item.Id || idx} className="mb-3 ps-3 border-start border-2 border-primary">
                      <div className="fs-4" ><strong>MPN:</strong> {item.Product_MPN__c ?? "N/A"}</div>
                      <div className="fs-4" ><strong>Qty:</strong> {item.Quantity}</div>
                      <div className="fs-4" ><strong>Price:</strong> ${item.TotalPrice?.toFixed(2) ?? "N/A"}</div>
                      <div className="fs-4" ><strong>Source:</strong> {item.Sales_Source__c ?? "N/A"}</div>
                      <a
                        href={`https://nethealthshopsllc.lightning.force.com/lightning/r/OrderItem/${item.Id}/view`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline-primary" size="sm" className="mt-2">
                          View in Salesforce
                        </Button>
                      </a>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-danger">No order information found.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>)}
    </Container>
  )
}

export default Returns